<script setup lang="ts">
import {computed, inject, onUpdated, ref, watch, watchEffect} from "vue";
import {Icon} from "@iconify/vue";
import {
    getNotifications as apiGetNotifications,
    markAllNotificationAsRead,
    markNotificationAsRead,
} from "@/Services/notifications";
import {Button, Dialog, Drawer} from "@/Components/Library";
import {Severity} from "@/Enums";
import useFirebase from "@/Composables/firebase";
import {useRouter} from "vue-router";
import {useToast} from "primevue/usetoast";
import {useMainStore} from "@/Stores/store";
import {useCookies} from "@vueuse/integrations/useCookies";
import axios from "axios";

const $store = useMainStore();
const $cookies = useCookies();

interface Notification {
    id: number;
    data: {
        text: string;
        route: [string, RouteParamsWithQueryOverload | RouteParam];
    };
    read_at: string;
    created_at: string;
}

const $echo = ref();
const notifications = ref([]);
const showDrawer = ref(false);
const emit = defineEmits(["toast"]);
const toast = useToast();
const router = useRouter();
const $date = inject<any>("date");
const unreadNotifications = computed(() =>
    notifications.value.filter((notification: Notification) => !notification.read_at),
);

const getNotifications = () =>
    apiGetNotifications().then((data) => {
        notifications.value = data;
        $store.notifications.updated_at = new Date();
    });

const markAllRead = () => {
    markAllNotificationAsRead().then((data) => {
        getNotifications();
    });
};

const notify = (notification: object) => {
    try {
        if ("Notification" in window && Notification.permission === "granted") {
            navigator.serviceWorker.ready.then((registration) => {
                registration.showNotification(notification?.title ?? import.meta.env.VITE_APP_NAME, {
                    body: notification?.body,
                    icon: "/images/icons/icon-192x192.png",
                    data: {
                        url: notification?.url ?? import.meta.env.VITE_APP_URL,
                    },
                });
            });
        }

        setAppBadge();
    } catch (e) {
        console.log(e);
    }
};
const handleClick = async (notification: Notification) => {
    console.log(notification);
    markNotificationAsRead(notification.id);
    notification.read_at = new Date().toISOString();
    showDrawer.value = false;
    if (notification.data?.route) {
        try {
            let route = JSON.parse(notification.data.route);
            await router.push(route);
        } catch (e) {
            console.log(e);
        }
    }
};

const setAppBadge = () => {
    if (navigator.setAppBadge && unreadNotifications.value.count) {
        // console.log("setting badge", unreadNotifications.value.count);
        navigator.setAppBadge(unreadNotifications.value.count);
    }

    if (navigator.clearAppBadge && !unreadNotifications.value.count) {
        // console.log("clearing badge");
        navigator.clearAppBadge();
    }
};
setAppBadge();

onUpdated(() => {
    setAppBadge();
});

const panel = ref();

const togglePanel = ($event) => {
    console.log($event);
    // router.reload({
    //     preserveState: true,
    //     preserveScroll: true,
    // });
    panel.value.toggle($event);
};

const notificationPermission = ref("Notification" in window ? Notification.permission : "denied");
const showNotificationAlert = computed({
    get: () => {
        return notificationPermission.value === "default" && $cookies.get("show-notification-alert") !== false;
    },
    set: (val) => {
        console.log(val);
        $cookies.set("show-notification-alert", val);
        notificationPermission.value = "granted";
    },
});
const requestPushNotificationPermissions = async () => {
    try {
        if ("Notification" in window && Notification.permission === "default") {
            const status = await Notification.requestPermission();
            if (status === "denied") {
                alert(
                    "You have denied permission to send notifications. You can enable them in your browser settings.",
                );
            }

            notificationPermission.value = status;
        } else {
            alert("Notifications are not supported in your browser.");
            $cookies.set("show-notification-alert", false);
        }
    } catch (e) {
        console.log(e);
    }
};

const notificationBody = (notification) =>
    notification.data.body ??
    notification.data.text ??
    notification.data.title ??
    notification.type.replace("App\\Notifications\\", "").replace("Notification", "") ??
    notification;

const appName = import.meta.env.VITE_APP_NAME;
watchEffect(() => {
    if ($store.user.id) {
        $echo.value = useEcho();
        $echo.value?.notification((notification) => {
            console.log("Echo", notification);
            // notifications.value.unshift({data: notification, read_at: null, created_at: new Date().toISOString()});
            // notify(notification);
            // toast.add({
            //     severity: notification.severity ?? "success",
            //     summary: notification.title ?? "Notification",
            //     detail: notification.text ?? "Unknown notification",
            //     life: 5000,
            //     position: "top-right"
            // });
            // getNotifications();
        });
        // console.log($store.user);
        useFirebase($store.user.params?.fcm_token, (payload) => {
            console.log("FCM CB", payload);
            notify(payload.data);
            toast.add({
                severity: payload.data?.severity ?? "success",
                summary: payload.notification.title ?? "Notification",
                detail: notificationBody(payload),
                life: 5000,
                position: "top-right",
            });
            getNotifications();
        })
            .then((data) => {
                // update users token in database
                axios.put(route("user.fcm-token"), {
                    fcm_token: data.token,
                });
            })
            .catch((e) => console.log(e));
        getNotifications();
    }
});
watch(
    () => $store.notifications.updated_at,
    () => {
        if (!$store.notifications.updated_at) {
            getNotifications();
        }
    },
);
</script>
<template>
    <div>
        <Button class="drop-shadow-black relative !p-0 !ring-0 drop-shadow-lg" @click="showDrawer = true">
            <Icon icon="solar:bell-bold" width="24" />
            <span
                v-if="unreadNotifications.length"
                class="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white"
            >
                {{ unreadNotifications.length }}
            </span>
        </Button>
        <!--        <Popover-->
        <!--            class="relative ml-auto w-full max-w-sm flex-col"-->
        <!--            ref="panel"-->
        <!--            pt:content:class="flex flex-col items-center justify-center p-2"-->
        <!--        >-->
        <!--        </Popover>-->
        <Drawer
            v-model:visible="showDrawer"
            header="Notifications"
            position="right"
            modal
            class="w-full max-w-64 lg:max-w-80"
        >
            <div class="flex grow flex-col" v-if="unreadNotifications.length">
                <div class="grow" v-auto-animate>
                    <template v-for="notification in unreadNotifications" :key="notification.id">
                        <button
                            @click="handleClick(notification)"
                            class="mb-1 block w-full cursor-pointer rounded border border-t-2 px-3 py-3 text-left text-sm transition dark:border-x-stone-700 dark:border-b-stone-700"
                            :class="[
                                `border-t-${Severity[notification.severity ?? 'info'].class} dark:border-t-${Severity[notification.severity ?? 'info'].class}`,
                            ]"
                        >
                            <p class="mb-1 text-xs font-normal text-stone-500 dark:text-stone-300">
                                {{ $date(notification.created_at).format("DD/MM/YYYY HH:mm") }}
                            </p>
                            <p class="text-xs text-stone-600 dark:text-stone-200">
                                {{ notificationBody(notification) }}
                            </p>
                        </button>
                    </template>
                </div>
            </div>
            <div v-else>
                <p class="px-3 py-2 text-center text-xs font-normal text-stone-600">No notifications</p>
            </div>
            <div class="mt-2 flex justify-center pt-2" v-if="unreadNotifications.length">
                <button
                    class="w-full rounded border px-4 py-1 text-xs transition dark:border-stone-700"
                    @click="markAllRead"
                    type="button"
                >
                    Mark all read
                </button>
            </div>
        </Drawer>
        <Dialog
            v-model:visible="showNotificationAlert"
            position="top"
            :modal="false"
            :draggable="false"
            class="mx-1 mt-1 w-full max-w-md border-0 border-none bg-black/80 text-white backdrop-blur"
        >
            <template #container="{closeCallback}">
                <div class="rounded-xl p-6">
                    <div>
                        {{ appName }} would like to send you notifications to keep you up to date with essential alerts
                    </div>
                    <div class="mt-6 flex justify-end">
                        <Button text class="mr-4" @click="closeCallback">Not now</Button>
                        <Button
                            class="rounded bg-askr-green px-4 py-1 font-bold"
                            @click="requestPushNotificationPermissions"
                        >
                            Allow
                        </Button>
                    </div>
                </div>
            </template>
        </Dialog>
    </div>
</template>
<style>
.p-popover:after,
.p-popover:before {
    display: none;
}
</style>
